// src/features/pdf/components/PDFToolWorkspace.tsx
import React, { ReactNode } from 'react';
import { Paper, Typography, Box, Stack } from '@mui/material';
import { Accept } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import FileDropzone from './FileDropzone';
import DraggableFileList from './DraggableFileList';

// Define a FileWithId interface to include a unique ID for each file
interface FileWithId {
    id: string;
    file: File;
}

interface PDFToolWorkspaceProps {
    titleKey: string;
    descKey: string;
    acceptedFiles: Accept;
    allowMultiple: boolean;
    allowReordering: boolean;

    // Update props to use FileWithId array
    files: FileWithId[];
    onDrop: (acceptedFiles: File[]) => void;
    onDelete: (fileId: string) => void;
    onReorder: (reorderedFiles: FileWithId[]) => void;
    onFileClick?: (file: File) => void;

    children?: ReactNode;
    disabled?: boolean;
}

const PDFToolWorkspace: React.FC<PDFToolWorkspaceProps> = ({
                                                               titleKey,
                                                               descKey,
                                                               acceptedFiles,
                                                               allowMultiple,
                                                               allowReordering,
                                                               files,
                                                               onDrop,
                                                               onDelete,
                                                               onReorder,
                                                               onFileClick,
                                                               children,
                                                               disabled = false,
                                                           }) => {
    const { t } = useTranslation();

    return (
        <Paper
            elevation={3}
            sx={{
                p: { xs: 2, md: 4 },
                width: '100%',
                maxWidth: '900px',
                bgcolor: 'background.paper',
                borderRadius: 2,
            }}
        >
            <Stack spacing={4}>
                <Box textAlign="center">
                    <Typography variant="h4" component="h1" gutterBottom>
                        {t(titleKey, { ns: 'tools' })}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t(descKey, { ns: 'tools' })}
                    </Typography>
                </Box>

                <FileDropzone
                    onDrop={onDrop}
                    accept={acceptedFiles}
                    multiple={allowMultiple}
                    disabled={disabled}
                />

                {files.length > 0 && (
                    <DraggableFileList
                        files={files}
                        onReorder={onReorder}
                        onDelete={onDelete}
                        onFileClick={onFileClick}
                        isDraggable={allowReordering}
                    />
                )}

                {children && <Box sx={{ width: '100%' }}>{children}</Box>}
            </Stack>
        </Paper>
    );
};

export default PDFToolWorkspace;